import React, { useEffect, useState } from 'react';
import LiquidityBridgeMonitor from './components/LiquidityBridgeMonitor.jsx';
import PowerLawNavigator from './components/PowerLawNavigator.jsx';
import CpiFfrSpreadWidget from './components/CpiFfrSpreadWidget.jsx';
import BondYieldCard from './components/BondYieldCard.jsx';
import WeeklyCycleSignal from './components/WeeklyCycleSignal.jsx';
import WalletAdoptionTracker from './components/WalletAdoptionTracker.jsx';
import M2CorrelationOverlay from './components/M2CorrelationOverlay.jsx';
import M2BitcoinLagTracker from './components/M2BitcoinLagTracker.jsx';
import LiquidityFlowMonitor from './components/LiquidityFlowMonitor.jsx';
import HodlerConviction from './components/HodlerConviction.jsx';
import BmoreWealthyAttribution from './components/BmoreWealthyAttribution.jsx';
import { loadConfig } from './config.js';
import { Bitcoin } from 'lucide-react';

/**
 * App
 * ---
 * Top-level shell. Loads runtime config (Lambda URLs from
 * amplify_outputs.json) before rendering anything that talks to the
 * proxies. While config is loading, shows a single-purpose splash.
 */
export default function App() {
  const [configReady, setConfigReady] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    loadConfig()
      .then(() => setConfigReady(true))
      .catch((e) => setConfigError(e.message ?? String(e)));
  }, []);

  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-bold text-red-400 mb-2">Failed to load config</h1>
          <p className="text-sm text-slate-400">{configError}</p>
        </div>
      </div>
    );
  }

  if (!configReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <div className="w-5 h-5 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
          <span className="text-sm font-mono">Connecting to live data sources…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-8">
        {/* Each section is wrapped in an anchored container so the header
            nav links work. scroll-mt-16 (= 64px) clears the 56px sticky
            header plus a small visual gutter when jumped to. */}
        {/* Compact at-a-glance ribbon — sits above the deeper analytics */}
        <section id="section-macro-ribbon" className="scroll-mt-16">
          <CpiFfrSpreadWidget />
        </section>
        <section id="section-bond-yields" className="scroll-mt-16">
          <BondYieldCard />
        </section>
        <section id="section-power-law" className="scroll-mt-16">
          <PowerLawNavigator />
        </section>
        <section id="section-cycle-signal" className="scroll-mt-16">
          <WeeklyCycleSignal />
        </section>
        <section id="section-wallets" className="scroll-mt-16">
          <WalletAdoptionTracker />
        </section>
        <section id="section-m2-correlation" className="scroll-mt-16">
          <M2CorrelationOverlay />
        </section>
        <section id="section-m2-btc-lag" className="scroll-mt-16">
          <M2BitcoinLagTracker />
        </section>
        <section id="section-liquidity-flow" className="scroll-mt-16">
          <LiquidityFlowMonitor />
        </section>
        <section id="section-hodler-conviction" className="scroll-mt-16">
          <HodlerConviction />
        </section>
        <section id="section-lead-lag" className="scroll-mt-16">
          <LiquidityBridgeMonitor />
        </section>
        {/* Creator attribution + soft support ask. Lives above the legal
            footer so the brand voice stays distinct from the disclaimer. */}
        <BmoreWealthyAttribution />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/80 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
            <Bitcoin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold tracking-widest">BTC MACRO</h1>
            <p className="text-[10px] text-slate-500 leading-none">Live FRED + market data</p>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-1 text-xs">
          <NavLink href="#section-bond-yields">Bond Yields</NavLink>
          <NavLink href="#section-power-law">Power Law</NavLink>
          <NavLink href="#section-cycle-signal">Cycle Signal</NavLink>
          <NavLink href="#section-wallets">Wallets</NavLink>
          <NavLink href="#section-m2-correlation">M2 Overlay</NavLink>
          <NavLink href="#section-m2-btc-lag">M2→BTC Lag</NavLink>
          <NavLink href="#section-lead-lag">Gold Signal</NavLink>
        </nav>
      </div>
    </header>
  );
}

function NavLink({ href, children }) {
  return (
    <a
      href={href}
      className="px-3 py-1.5 rounded-md text-slate-300 hover:bg-white/5 transition-colors"
    >
      {children}
    </a>
  );
}

function Footer() {
  return (
    <footer className="mt-16 border-t border-white/10 px-6 py-8 text-center text-xs text-slate-500 leading-relaxed">
      <p>
        Live data: Yahoo Finance (BTC spot, BTC + gold + S&amp;P 500 + Nasdaq history) with CoinGecko fallback,
        FRED / Federal Reserve Bank of St. Louis (CPI, PPI, Fed Funds Rate, 10Y Treasury, 10Y-2Y spread, M2 Money Supply),
        Dune Analytics (on-chain wallet activity ≥ 0.01 BTC), Blockchain.com (all-activity baseline),
        multpl.com (S&amp;P 500 trailing P/E).
      </p>
      <p className="mt-2 text-slate-600">
        ⚠️ Educational purposes only — not financial advice. All projections are heuristic.
      </p>
    </footer>
  );
}
